import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Brain,
  Lightbulb,
  Target,
  Clock,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Info,
  ChevronRight,
  Play,
  Calendar,
  BarChart3,
  RefreshCw,
  Bookmark,
  BookmarkCheck,
  Zap,
  Activity
} from 'lucide-react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer
} from 'recharts';

import { coachingAPI } from '@/services/api';
import { toast } from '@/hooks/use-toast';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Drill suggestion with difficulty level */
interface DrillSuggestion {
  name: string;
  duration: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

/** Immediate recommendation (for compact/analytics view) */
interface ImmediateRecommendation {
  id: string;
  category: 'technique' | 'mental' | 'fitness' | 'strategy';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  evidence: string[];
  expectedImprovement: string;
  drillSuggestions: DrillSuggestion[];
}

/** Short-term recommendation with milestones */
interface ShortTermRecommendation {
  id: string;
  category: 'technique' | 'mental' | 'fitness' | 'strategy';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  timeline: string;
  milestones: string[];
}

/** Long-term recommendation with strategic goals */
interface LongTermRecommendation {
  id: string;
  category: 'technique' | 'mental' | 'fitness' | 'strategy' | 'leadership';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  timeline: string;
  goals: string[];
}

/** Pre-structured recommendations data (compact/analytics view) */
interface StructuredRecommendationsData {
  immediate: ImmediateRecommendation[];
  shortTerm: ShortTermRecommendation[];
  longTerm: LongTermRecommendation[];
}

/** AI recommendation from API (full/coach view) */
interface AIRecommendation {
  id: string;
  category: 'technique' | 'training' | 'performance' | 'mental' | 'tactical' | 'fitness' | 'strategy' | 'leadership';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  reasoning?: string;
  evidence: string[];
  expectedOutcome?: string;
  expectedImprovement?: string;
  timeframe?: string;
  timeline?: string;
  confidence?: number;
  actionSteps?: string[];
  drillSuggestions?: string[] | DrillSuggestion[];
  relatedMetrics?: string[];
  milestones?: string[];
  goals?: string[];
  isBookmarked?: boolean;
}

/** Performance analysis data for full view */
interface PerformanceAnalysis {
  summary: string;
  averages: {
    exitVelocity: number;
    distance: number;
    powerTransfer: number;
    sweetSpotAccuracy: number;
  };
  trends: {
    exitVelocity: number;
    distance: number;
    powerTransfer: number;
  };
  strengths: string[];
  weaknesses: string[];
  excellentPercentage: number;
  goodOrBetterPercentage: number;
}

/** Technique analysis data for full view */
interface TechniqueAnalysis {
  overallScore: number;
  areas: {
    timing: number;
    balance: number;
    footwork: number;
    batSwing: number;
    followThrough: number;
  };
  recommendations: string[];
}

/** Player info for display */
interface Player {
  id?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
}

// ============================================================================
// PROPS INTERFACE
// ============================================================================

interface AIRecommendationsProps {
  /** Player ID for fetching recommendations */
  playerId?: string;
  /** Session ID for context */
  sessionId?: string;
  /** Display variant */
  variant?: 'compact' | 'full' | 'dashboard';
  /** Show action buttons */
  showActions?: boolean;
  /** Callback when recommendation is applied */
  onRecommendationApply?: (rec: AIRecommendation | ImmediateRecommendation) => void;
  /** Pre-loaded recommendations (structured format for compact view) */
  data?: StructuredRecommendationsData;
  /** Pre-loaded flat recommendations (for full view) */
  recommendations?: AIRecommendation[];
  /** Whether to fetch data on mount */
  fetchOnMount?: boolean;
  /** Player object for display */
  player?: Player;
  /** Trigger data refresh */
  refreshKey?: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const categoryIcons = {
  technique: Target,
  mental: Brain,
  fitness: TrendingUp,
  strategy: Lightbulb,
  leadership: CheckCircle,
  training: Activity,
  performance: BarChart3,
  tactical: Target
};

const categoryColors: Record<string, string> = {
  technique: 'bg-blue-100 text-blue-800 border-blue-200',
  mental: 'bg-purple-100 text-purple-800 border-purple-200',
  fitness: 'bg-green-100 text-green-800 border-green-200',
  strategy: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  leadership: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  training: 'bg-green-100 text-green-800 border-green-200',
  performance: 'bg-purple-100 text-purple-800 border-purple-200',
  tactical: 'bg-orange-100 text-orange-800 border-orange-200'
};

const priorityColors: Record<string, string> = {
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-orange-100 text-orange-800 border-orange-200',
  low: 'bg-gray-100 text-gray-800 border-gray-200'
};

const difficultyColors: Record<string, string> = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  hard: 'bg-red-100 text-red-700'
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getCategoryIcon(category: string) {
  const Icon = categoryIcons[category as keyof typeof categoryIcons] || Target;
  return <Icon className="h-4 w-4" />;
}

function getPriorityIcon(priority: string) {
  switch (priority) {
    case 'high':
      return <AlertTriangle className="w-4 h-4 text-red-600" />;
    case 'medium':
      return <Clock className="w-4 h-4 text-yellow-600" />;
    case 'low':
      return <CheckCircle className="w-4 h-4 text-gray-600" />;
    default:
      return <CheckCircle className="w-4 h-4 text-gray-600" />;
  }
}

// ============================================================================
// COMPACT VIEW COMPONENT (Tab-based, for analytics page)
// ============================================================================

interface CompactViewProps {
  data: StructuredRecommendationsData;
  showActions?: boolean;
  onRecommendationApply?: (rec: ImmediateRecommendation) => void;
}

function CompactView({ data, showActions, onRecommendationApply }: CompactViewProps) {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const toggleCard = (id: string) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <div className="space-y-6">
      {/* AI Insights Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-6 w-6 text-purple-600" />
            AI-Powered Coaching Insights
          </CardTitle>
          <CardDescription>
            Personalized recommendations based on your performance data, technique analysis, and improvement patterns
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs defaultValue="immediate" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="immediate" className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Immediate Focus
          </TabsTrigger>
          <TabsTrigger value="shortTerm" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Short-term Goals
          </TabsTrigger>
          <TabsTrigger value="longTerm" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Long-term Development
          </TabsTrigger>
        </TabsList>

        {/* Immediate Recommendations */}
        <TabsContent value="immediate" className="space-y-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Next Session Focus</h3>
            <p className="text-muted-foreground">
              Priority recommendations for your upcoming training sessions
            </p>
          </div>

          {data.immediate.map((recommendation) => (
            <Card key={recommendation.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className={categoryColors[recommendation.category]}>
                        {getCategoryIcon(recommendation.category)}
                        <span className="ml-1 capitalize">{recommendation.category}</span>
                      </Badge>
                      <Badge variant="outline" className={priorityColors[recommendation.priority]}>
                        {recommendation.priority.toUpperCase()} PRIORITY
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{recommendation.title}</CardTitle>
                    <CardDescription>{recommendation.description}</CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleCard(recommendation.id)}
                  >
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${
                        expandedCard === recommendation.id ? 'rotate-90' : ''
                      }`}
                    />
                  </Button>
                </div>
              </CardHeader>

              {expandedCard === recommendation.id && (
                <CardContent className="pt-0">
                  <Separator className="mb-4" />

                  {/* Evidence */}
                  <div className="space-y-3 mb-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Info className="h-4 w-4 text-blue-600" />
                      Supporting Evidence
                    </h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {recommendation.evidence.map((evidence, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                          {evidence}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Expected Improvement */}
                  <div className="space-y-2 mb-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      Expected Improvement
                    </h4>
                    <p className="text-sm text-green-700 bg-green-50 p-2 rounded">
                      {recommendation.expectedImprovement}
                    </p>
                  </div>

                  {/* Drill Suggestions */}
                  <div className="space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Play className="h-4 w-4 text-purple-600" />
                      Recommended Drills
                    </h4>
                    <div className="grid gap-2">
                      {recommendation.drillSuggestions.map((drill, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium">{drill.name}</div>
                            <div className="text-sm text-muted-foreground">{drill.duration}</div>
                          </div>
                          <Badge variant="outline" className={difficultyColors[drill.difficulty]}>
                            {drill.difficulty}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Apply Action */}
                  {showActions && onRecommendationApply && (
                    <div className="mt-4 pt-4 border-t">
                      <Button
                        onClick={() => onRecommendationApply(recommendation)}
                        className="w-full"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Apply This Recommendation
                      </Button>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </TabsContent>

        {/* Short-term Recommendations */}
        <TabsContent value="shortTerm" className="space-y-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">2-6 Week Development Plan</h3>
            <p className="text-muted-foreground">
              Progressive goals to achieve over the coming weeks
            </p>
          </div>

          {data.shortTerm.map((recommendation) => (
            <Card key={recommendation.id} className="overflow-hidden">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className={categoryColors[recommendation.category]}>
                        {getCategoryIcon(recommendation.category)}
                        <span className="ml-1 capitalize">{recommendation.category}</span>
                      </Badge>
                      <Badge variant="outline" className={priorityColors[recommendation.priority]}>
                        {recommendation.priority.toUpperCase()} PRIORITY
                      </Badge>
                      <Badge variant="outline">
                        <Clock className="h-3 w-3 mr-1" />
                        {recommendation.timeline}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{recommendation.title}</CardTitle>
                    <CardDescription>{recommendation.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    Key Milestones
                  </h4>
                  <div className="space-y-2">
                    {recommendation.milestones.map((milestone, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-600">
                          {index + 1}
                        </div>
                        <span className="text-sm">{milestone}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Long-term Recommendations */}
        <TabsContent value="longTerm" className="space-y-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Season & Career Development</h3>
            <p className="text-muted-foreground">
              Strategic long-term goals for sustained improvement and mastery
            </p>
          </div>

          {data.longTerm.map((recommendation) => (
            <Card key={recommendation.id} className="overflow-hidden">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className={categoryColors[recommendation.category]}>
                        {getCategoryIcon(recommendation.category)}
                        <span className="ml-1 capitalize">{recommendation.category}</span>
                      </Badge>
                      <Badge variant="outline" className={priorityColors[recommendation.priority]}>
                        {recommendation.priority.toUpperCase()} PRIORITY
                      </Badge>
                      <Badge variant="outline">
                        <Calendar className="h-3 w-3 mr-1" />
                        {recommendation.timeline}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{recommendation.title}</CardTitle>
                    <CardDescription>{recommendation.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Target className="h-4 w-4 text-green-600" />
                    Strategic Goals
                  </h4>
                  <div className="space-y-2">
                    {recommendation.goals.map((goal, index) => (
                      <div key={index} className="flex items-start gap-3 p-2 bg-gray-50 rounded">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{goal}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* AI Confidence Footer */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              <span>Recommendations based on 1,250+ data points</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span>94% prediction accuracy</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// FULL VIEW COMPONENT (Coach-style with charts and filtering)
// ============================================================================

interface FullViewProps {
  playerId: string;
  player?: Player;
  refreshKey?: number;
  recommendations?: AIRecommendation[];
  fetchOnMount?: boolean;
  showActions?: boolean;
  onRecommendationApply?: (rec: AIRecommendation) => void;
}

function FullView({
  playerId,
  player,
  refreshKey = 0,
  recommendations: propRecommendations,
  fetchOnMount = true,
  showActions,
  onRecommendationApply
}: FullViewProps) {
  const [recommendations, setRecommendations] = useState<AIRecommendation[]>(propRecommendations || []);
  const [performanceAnalysis, setPerformanceAnalysis] = useState<PerformanceAnalysis | null>(null);
  const [techniqueAnalysis, setTechniqueAnalysis] = useState<TechniqueAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(fetchOnMount && !propRecommendations);
  const [filter, setFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('priority');

  useEffect(() => {
    if (propRecommendations) {
      setRecommendations(propRecommendations);
      setIsLoading(false);
    } else if (fetchOnMount && playerId) {
      loadRecommendations();
    }
  }, [playerId, refreshKey, propRecommendations, fetchOnMount]);

  const loadRecommendations = async () => {
    try {
      setIsLoading(true);
      const response = await coachingAPI.getRecommendations(playerId);

      setRecommendations(response.recommendations || []);
      setPerformanceAnalysis(response.analysis?.performance || null);
      setTechniqueAnalysis(response.analysis?.technique || null);
    } catch (error) {
      console.error('Failed to load recommendations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load AI recommendations. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadRecommendations();
    toast({
      title: 'Recommendations Updated',
      description: 'AI recommendations have been refreshed with latest data.',
    });
  };

  const handleBookmark = (recommendationId: string) => {
    setRecommendations(prev =>
      prev.map(rec =>
        rec.id === recommendationId
          ? { ...rec, isBookmarked: !rec.isBookmarked }
          : rec
      )
    );

    toast({
      title: 'Bookmark Updated',
      description: 'Recommendation bookmark status has been updated.',
    });
  };

  const filteredAndSortedRecommendations = useMemo(() => {
    return recommendations
      .filter(rec => {
        if (filter === 'all') return true;
        if (filter === 'bookmarked') return rec.isBookmarked;
        return rec.category === filter;
      })
      .sort((a, b) => {
        if (sortBy === 'priority') {
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
        }
        if (sortBy === 'confidence') {
          return (b.confidence || 0) - (a.confidence || 0);
        }
        return 0;
      });
  }, [recommendations, filter, sortBy]);

  // Prepare technique radar chart data
  const techniqueRadarData = useMemo(() => {
    if (!techniqueAnalysis) return [];
    return [
      { subject: 'Timing', value: techniqueAnalysis.areas.timing },
      { subject: 'Balance', value: techniqueAnalysis.areas.balance },
      { subject: 'Footwork', value: techniqueAnalysis.areas.footwork },
      { subject: 'Bat Swing', value: techniqueAnalysis.areas.batSwing },
      { subject: 'Follow Through', value: techniqueAnalysis.areas.followThrough },
    ];
  }, [techniqueAnalysis]);

  const playerName = player?.firstName && player?.lastName
    ? `${player.firstName} ${player.lastName}`
    : player?.name || 'Selected Player';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Generating AI insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <Brain className="w-6 h-6 mr-2" />
            AI-Powered Insights
          </h2>
          <p className="text-muted-foreground">
            Intelligent recommendations for {playerName}
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="technique">Technique</SelectItem>
              <SelectItem value="training">Training</SelectItem>
              <SelectItem value="performance">Performance</SelectItem>
              <SelectItem value="mental">Mental</SelectItem>
              <SelectItem value="tactical">Tactical</SelectItem>
              <SelectItem value="bookmarked">Bookmarked</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="priority">Priority</SelectItem>
              <SelectItem value="confidence">Confidence</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Performance Analysis Overview */}
      {performanceAnalysis && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Performance Metrics */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                Performance Analysis
              </CardTitle>
              <CardDescription>{performanceAnalysis.summary}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {performanceAnalysis.averages.exitVelocity.toFixed(1)}
                  </div>
                  <div className="text-sm text-muted-foreground">Exit Velocity</div>
                  <div className="text-xs text-muted-foreground">
                    {performanceAnalysis.trends.exitVelocity > 0 ? (
                      <span className="text-green-600 flex items-center justify-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        +{performanceAnalysis.trends.exitVelocity.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center justify-center">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        {performanceAnalysis.trends.exitVelocity.toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {performanceAnalysis.averages.distance.toFixed(1)}m
                  </div>
                  <div className="text-sm text-muted-foreground">Distance</div>
                  <div className="text-xs text-muted-foreground">
                    {performanceAnalysis.trends.distance > 0 ? (
                      <span className="text-green-600 flex items-center justify-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        +{performanceAnalysis.trends.distance.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center justify-center">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        {performanceAnalysis.trends.distance.toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {(performanceAnalysis.averages.powerTransfer * 100).toFixed(0)}%
                  </div>
                  <div className="text-sm text-muted-foreground">Power Transfer</div>
                  <div className="text-xs text-muted-foreground">
                    {performanceAnalysis.trends.powerTransfer > 0 ? (
                      <span className="text-green-600 flex items-center justify-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        +{performanceAnalysis.trends.powerTransfer.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center justify-center">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        {performanceAnalysis.trends.powerTransfer.toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {(performanceAnalysis.averages.sweetSpotAccuracy * 100).toFixed(0)}%
                  </div>
                  <div className="text-sm text-muted-foreground">Accuracy</div>
                  <div className="text-xs text-muted-foreground">Sweet spot</div>
                </div>
              </div>

              <Separator className="my-4" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-green-700 mb-2 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Strengths
                  </h4>
                  <ul className="space-y-1">
                    {performanceAnalysis.strengths.map((strength, index) => (
                      <li key={index} className="text-sm text-green-600">
                        • {strength}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-orange-700 mb-2 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Areas for Improvement
                  </h4>
                  <ul className="space-y-1">
                    {performanceAnalysis.weaknesses.map((weakness, index) => (
                      <li key={index} className="text-sm text-orange-600">
                        • {weakness}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Technique Analysis */}
          {techniqueAnalysis && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Activity className="w-5 h-5 mr-2" />
                  Technique Analysis
                </CardTitle>
                <CardDescription>
                  Overall Score: {techniqueAnalysis.overallScore}/100
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <RadarChart data={techniqueRadarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar
                      name="Technique"
                      dataKey="value"
                      stroke="#3b82f6"
                      fill="#3b82f6"
                      fillOpacity={0.3}
                    />
                  </RadarChart>
                </ResponsiveContainer>

                <div className="mt-4 space-y-2">
                  {Object.entries(techniqueAnalysis.areas).map(([area, score]) => (
                    <div key={area} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="capitalize">{area.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="font-medium">{score}/100</span>
                      </div>
                      <Progress value={score} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* AI Recommendations */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            AI Recommendations ({filteredAndSortedRecommendations.length})
          </h3>
        </div>

        {filteredAndSortedRecommendations.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Brain className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No Recommendations Available</h3>
              <p className="mt-2 text-muted-foreground">
                {filter === 'all'
                  ? 'AI is analyzing performance data to generate recommendations.'
                  : `No recommendations found for ${filter}.`
                }
              </p>
              <Button className="mt-4" onClick={handleRefresh}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Generate Recommendations
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredAndSortedRecommendations.map(rec => (
            <Card key={rec.id} className="relative">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      {getPriorityIcon(rec.priority)}
                      <CardTitle className="text-lg">{rec.title}</CardTitle>
                      <Badge className={categoryColors[rec.category] || 'bg-gray-100 text-gray-800'}>
                        {rec.category}
                      </Badge>
                      <Badge className={priorityColors[rec.priority]}>
                        {rec.priority} priority
                      </Badge>
                    </div>
                    <CardDescription>{rec.description}</CardDescription>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleBookmark(rec.id)}
                    className={rec.isBookmarked ? 'text-yellow-600' : 'text-gray-400'}
                  >
                    {rec.isBookmarked ? (
                      <BookmarkCheck className="w-4 h-4" />
                    ) : (
                      <Bookmark className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Confidence and Timeframe */}
                {(rec.confidence !== undefined || rec.timeframe) && (
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-4">
                      {rec.confidence !== undefined && (
                        <div>
                          <span className="text-muted-foreground">Confidence: </span>
                          <span className="font-medium">{rec.confidence}%</span>
                        </div>
                      )}
                      {rec.timeframe && (
                        <div>
                          <span className="text-muted-foreground">Timeframe: </span>
                          <span className="font-medium">{rec.timeframe}</span>
                        </div>
                      )}
                    </div>
                    {rec.confidence !== undefined && (
                      <Progress value={rec.confidence} className="w-20 h-2" />
                    )}
                  </div>
                )}

                <Separator />

                {/* Reasoning and Evidence */}
                <div className="space-y-3">
                  {rec.reasoning && (
                    <div>
                      <h4 className="font-medium mb-1 flex items-center">
                        <Lightbulb className="w-4 h-4 mr-1" />
                        AI Reasoning
                      </h4>
                      <p className="text-sm text-muted-foreground">{rec.reasoning}</p>
                    </div>
                  )}

                  {rec.evidence && rec.evidence.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-1 flex items-center">
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Supporting Evidence
                      </h4>
                      <ul className="space-y-1">
                        {rec.evidence.map((evidence, index) => (
                          <li key={index} className="text-sm text-muted-foreground">
                            • {evidence}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(rec.expectedOutcome || rec.expectedImprovement) && (
                    <div>
                      <h4 className="font-medium mb-1 flex items-center">
                        <Target className="w-4 h-4 mr-1" />
                        Expected Outcome
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {rec.expectedOutcome || rec.expectedImprovement}
                      </p>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Action Steps */}
                {rec.actionSteps && rec.actionSteps.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center">
                      <Zap className="w-4 h-4 mr-1" />
                      Action Steps
                    </h4>
                    <ol className="space-y-1">
                      {rec.actionSteps.map((step, index) => (
                        <li key={index} className="text-sm flex items-start">
                          <span className="font-medium text-primary mr-2">{index + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Drill Suggestions */}
                {rec.drillSuggestions && rec.drillSuggestions.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium mb-2">Recommended Drills</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {rec.drillSuggestions.map((drill, index) => (
                          <Badge key={index} variant="outline" className="justify-start">
                            {typeof drill === 'string' ? drill : drill.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Related Metrics */}
                {rec.relatedMetrics && rec.relatedMetrics.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium mb-2">Related Metrics to Track</h4>
                      <div className="flex flex-wrap gap-2">
                        {rec.relatedMetrics.map((metric, index) => (
                          <Badge key={index} variant="secondary">
                            {metric}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Apply Action */}
                {showActions && onRecommendationApply && (
                  <>
                    <Separator />
                    <Button
                      onClick={() => onRecommendationApply(rec)}
                      className="w-full"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Apply This Recommendation
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// DASHBOARD VIEW COMPONENT (Summary view for quick insights)
// ============================================================================

interface DashboardViewProps {
  playerId?: string;
  recommendations?: AIRecommendation[];
  data?: StructuredRecommendationsData;
}

function DashboardView({ playerId: _playerId, recommendations, data }: DashboardViewProps) {
  // Get top 3 recommendations to display
  const topRecommendations = useMemo(() => {
    if (recommendations) {
      return recommendations
        .filter(r => r.priority === 'high')
        .slice(0, 3);
    }
    if (data) {
      return data.immediate.slice(0, 3);
    }
    return [];
  }, [recommendations, data]);

  if (topRecommendations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-600" />
            AI Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-4">
            No recommendations available
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-purple-600" />
          Top AI Insights
        </CardTitle>
        <CardDescription>
          Priority recommendations for immediate focus
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {topRecommendations.map((rec: any, index: number) => (
          <div
            key={rec.id || index}
            className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
          >
            <div className="flex-shrink-0">
              {getPriorityIcon(rec.priority)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{rec.title}</p>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {rec.description}
              </p>
            </div>
            <Badge
              variant="outline"
              className={categoryColors[rec.category] || 'bg-gray-100'}
            >
              {rec.category}
            </Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AIRecommendations({
  playerId,
  sessionId: _sessionId,
  variant = 'compact',
  showActions = false,
  onRecommendationApply,
  data,
  recommendations,
  fetchOnMount = true,
  player,
  refreshKey = 0
}: AIRecommendationsProps) {
  // For compact variant, use structured data
  if (variant === 'compact' && data) {
    return (
      <CompactView
        data={data}
        showActions={showActions}
        onRecommendationApply={onRecommendationApply as any}
      />
    );
  }

  // For dashboard variant, show summary view
  if (variant === 'dashboard') {
    return (
      <DashboardView
        playerId={playerId}
        recommendations={recommendations}
        data={data}
      />
    );
  }

  // For full variant (or compact without data), use full view with fetching
  if (playerId) {
    return (
      <FullView
        playerId={playerId}
        player={player}
        refreshKey={refreshKey}
        recommendations={recommendations}
        fetchOnMount={fetchOnMount && !recommendations}
        showActions={showActions}
        onRecommendationApply={onRecommendationApply as any}
      />
    );
  }

  // Fallback: no data and no playerId - show empty state
  return (
    <Card>
      <CardContent className="p-8 text-center">
        <Brain className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-semibold">No Data Available</h3>
        <p className="mt-2 text-muted-foreground">
          Please provide player data or a player ID to view recommendations.
        </p>
      </CardContent>
    </Card>
  );
}

// Named export for backward compatibility
export default AIRecommendations;
